<!-- eslint-disable no-debugger -->
<template>
  <cube-page class="orig-sales-flow-card" :title="currentTitle">
    <!-- 右侧筛选按钮 -->
    <span slot="right" class="filter-icon" @click="showDrawer">
      <span class="sprite icon-filter-blue"></span>
    </span>

    <!-- 内容区域 -->
    <div slot="content" class="page-content">
      <form-card-list
        :key="currentDataFlowType"
        ref="cardList"
        :select="select"
        :filterFields="currentFilterFields"
        :searchConfig="searchConfig"
        :order="order"
        :formKey="formKey"
        :rowKey="rowKey"
        :title="currentTitle"
        :customDataFetch="fetchCardData"
        :manualMode="manualMode"
        :manualColumns="manualColumns"
        :manualFilters="currentManualFilters"
        :pageSize="20"
        detailPageCacheName="FormTableDetail"
        @meta-loaded="handleMetaLoaded"
        @card-click="handleCardClick"
      >
        <!-- 自定义卡片内容 -->
        <template #card="{ item }">
          <div class="sales-flow-card">
            <div class="card-header">
              <div class="header-top">
                <div class="flow-code"></div>
                <div class="flow-date" v-if="flowConfig.metaKey === 'original_sale'">{{ item.channelTime || '-' }}</div>
                <div class="flow-date" v-else-if="flowConfig.metaKey === 'original_purchase'">{{ item.purchaseTime || '-' }}</div>
                <div class="flow-date" v-else-if="flowConfig.metaKey === 'original_inventory'">{{ item.inventoryTime || '-' }}</div>
              </div>
            </div>

            <div class="card-body">
              <div class="customer-section">
                <div class="customer-main">
                  <van-icon name="manager-o" color="#5b8ff9" size="18" />
                  <span class="customer-name" v-if="flowConfig.metaKey === 'original_sale'">{{ `${item.ddiSellerName || '-'}(${item.ddiSellerCode || '-'})` }}</span>
                  <span class="customer-name" v-else-if="flowConfig.metaKey === 'original_purchase'">{{ `${item.ddiBuyerName || '-'}(${item.ddiBuyerCode || '-'})` }}</span>
                  <span class="customer-name" v-else-if="flowConfig.metaKey === 'original_inventory'">{{ `${item.ddiCustomerName || '-'}(${item.ddiCustomerCode || '-'})` }}</span>
                </div>
                <div class="customer-flow">
                  <div class="flow-label" :style="{ width: flowConfig.sellerLabel }">{{ flowConfig.sellerLabel }}:</div>
                  <span class="flow-value" v-if="flowConfig.metaKey === 'original_sale'">{{ item.companyName || '-' }}</span>
                  <span class="flow-value" v-else-if="flowConfig.metaKey === 'original_purchase'">{{ item.companyName || '-' }}</span>
                  <span class="flow-value" v-else-if="flowConfig.metaKey === 'original_inventory'">{{ item.companyName || '-' }}</span>
                </div>
                <div v-if="flowConfig.showBuyer" class="customer-flow">
                  <div class="flow-label" v-if="flowConfig.buyerLabel">{{ flowConfig.buyerLabel }}:</div>
                  <span class="flow-value" v-if="flowConfig.metaKey === 'original_sale'">{{ item.buyerName || '-' }}</span>
                  <span class="flow-value" v-else-if="flowConfig.metaKey === 'original_purchase'">{{ item.sellerName || '-' }}</span>
                </div>
              </div>

              <div class="product-section">
                <div class="product-main">
                  <van-icon name="bag-o" color="#5b8ff9" size="18" />
                  <span class="product-name">{{ item.productName || '-' }}</span>
                </div>
                <div class="product-detail">
                  <span>{{ item.spec || '-' }}</span>
                  <span class="separator">·</span>
                  <span>批号: {{ item.batchNumber || '-' }}</span>
                </div>
              </div>

              <div class="data-highlight">
                <div class="data-item">
                  <div class="data-value">{{ item.count ? formatNumber('count', item.count) : item.count }}</div>
                  <div class="data-label">数量</div>
                </div>
                <div class="data-item">
                  <div class="data-value">{{ item.price ? formatNumber('price', item.price) : item.price }}</div>
                  <div class="data-label">单价</div>
                </div>
                <div class="data-item">
                  <div class="data-value highlight">{{ item.totalPrice ? formatNumber('totalPrice', item.totalPrice) : item.totalPrice }}</div>
                  <div class="data-label">金额</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </form-card-list>

      <!-- 筛选抽屉 -->
      <drawer ref="drawer">
        <search-box
          v-if="searchList.length > 0"
          ref="searchBox"
          :searchList="searchList"
          :searchConfig="searchConfig"
          @search="handleSearch"
          @reset="handleReset"
        ></search-box>
      </drawer>
    </div>
  </cube-page>
</template>

<script>
import FormCardList from './components/FormCardList'
import CubePage from '@/components/cube-page'
import Drawer from '@/components/drawer'
import SearchBox from '@/components/search-box'
import {
  getData,
  areaTree,
  companySalesNameList,
  productSalesNameList,
  customerSalesNameList,
  companyPurchaseNameList,
  productPurchaseNameList,
  customerPurchaseNameList,
  suppliersPurchaseNameList
} from '@/api/tjls/index'
import mockMeta from './mockMeta/terminal-sales-analysis'

const FLOW_TYPE_CONFIG = {
  sales: {
    title: '终端销售',
    metaKey: 'original_sale',
    filterFields: [
      'dataFlowType',
      'beginTime',
      'endTime',
      'customerProvince',
      'agencyKeys',
      'companyNames',
      'products',
      'customs'
    ],
    sellerLabel: '卖方客户',
    sellerStyle: '120px',
    buyerLabel: '买方客户',
    showBuyer: true,
    showPrice: true
  },
  purchase: {
    title: '终端采购',
    metaKey: 'original_purchase',
    filterFields: [
      'dataFlowType',
      'beginTime',
      'endTime',
      'customerProvince',
      'agencyKeys',
      'companyNames',
      'products',
      'suppliers'
    ],
    sellerLabel: '采购商名称',
    sellerStyle: '140px',
    buyerLabel: '供应商名称',
    showBuyer: true,
    showPrice: true
  },
  inventory: {
    title: '终端库存',
    metaKey: 'original_inventory',
    filterFields: [
      'dataFlowType',
      'beginTime',
      'endTime',
      'customerProvince',
      'agencyKeys',
      'products'
    ],
    sellerLabel: '库存客户名称',
    sellerStyle: '160px',
    buyerLabel: '',
    showBuyer: true,
    showPrice: true
  }
}

export default {
  name: 'TerminalSalesAnalysis',
  components: { CubePage, FormCardList, Drawer, SearchBox },
  data() {
    return {
      currentDataFlowType: 'sales',
      pendingSearchParams: null,
      filterOptionsCache: {},
      searchConfig: {
        dataFlowType: {
          isNotClearable: true,
          defaultValue: 'sales'
        },
        beginTime: {
          isNotClearable: true,
          defaultValue: this.$dayjs().startOf('year').format('YYYY-MM-DD'),
          inventoryDefaultValue: this.$dayjs().format('YYYY-MM-DD')
        },
        endTime: {
          isNotClearable: true,
          defaultValue: this.$dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
          inventoryDefaultValue: this.$dayjs().format('YYYY-MM-DD')
        }
      },
      select: [],
      order: [],
      rowKey: 'flowCode',
      searchList: [],
      manualColumns: {
        count: {
          label: '数量',
          format: {
            magnitude: 'default',
            toFixed: 0,
            thousandsSeparatorFlag: true,
            emptyValue: '-'
          }
        },
        price: {
          label: '单价',
          format: {
            magnitude: 'default',
            toFixed: 2,
            thousandsSeparatorFlag: true,
            emptyValue: '-'
          }
        },
        totalPrice: {
          label: '金额',
          format: {
            magnitude: 'default',
            toFixed: 2,
            thousandsSeparatorFlag: true,
            emptyValue: '-'
          }
        }
      },
      manualMode: true
    }
  },
  beforeRouteEnter(to, from, next) {
    if (from.name === 'FormTableDetail') {
      to.meta.isUseCache = true
    } else {
      to.meta.isUseCache = false
    }
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'FormTableDetail') {
      from.meta.keepAlive = true
      next()
    } else {
      from.meta.keepAlive = false
      next()
    }
  },
  activated() {
    if (this.$route.meta.isUseCache) {
      this.$nextTick(() => {
        const cardList = this.$refs.cardList
        if (cardList) {
          cardList.restoreScroll()
        }
      })
    }
  },
  computed: {
    flowConfig() {
      return FLOW_TYPE_CONFIG[this.currentDataFlowType] || FLOW_TYPE_CONFIG.sales
    },
    currentTitle() {
      return this.flowConfig.title
    },
    currentFilterFields() {
      return this.flowConfig.filterFields
    },
    currentManualFilters() {
      return Object.values(mockMeta[this.flowConfig.metaKey].filters)
    },
    formKey() {
      return this.flowConfig.metaKey
    }
  },
  methods: {
    async fetchCardData({ pageNum, pageSize, formModel }) {
      const res = await getData(this.flowConfig.metaKey, {
        pageSize: pageSize,
        page: pageNum,
        ...formModel
      })
      return {
        items: res.data.items || [],
        totalCount: res.data.totalCount || 0
      }
    },
    // 显示筛选抽屉
    async showDrawer() {
      const flowType = this.normalizeFlowType(this.currentDataFlowType)
      if (!this.filterOptionsCache[flowType]) {
        await this.ensureFilterOptions(flowType)
        await this.rebuildSearchList(flowType)
      }
      this.$refs.drawer.show()
    },

    getEmptyFilterOptions() {
      return {
        customerProvince: [],
        agencyKeys: [],
        companyNames: [],
        products: [],
        customs: [],
        suppliers: []
      }
    },

    getFilterOptionsForFlowType(flowType) {
      const normalizedFlowType = this.normalizeFlowType(flowType)
      return this.filterOptionsCache[normalizedFlowType] || this.getEmptyFilterOptions()
    },

    formatDataEndDate(value) {
      if (value == null || value === '') {
        return ''
      }
      const date = this.$dayjs(value)
      return date.isValid() ? date.format('YYYY-MM-DD') : ''
    },

    getEndTimeDefault(flowType = this.currentDataFlowType) {
      if (flowType === 'inventory') {
        return this.searchConfig.beginTime.inventoryDefaultValue
      }
      const dataEndDate = this.getFilterOptionsForFlowType(flowType).dataEndDate
      return dataEndDate || this.searchConfig.endTime.defaultValue
    },

    applyEndTimeDefault(flowType = this.currentDataFlowType) {
      const normalizedFlowType = this.normalizeFlowType(flowType)
      const dataEndDate = this.getFilterOptionsForFlowType(normalizedFlowType).dataEndDate
      if (dataEndDate && normalizedFlowType !== 'inventory') {
        this.searchConfig.endTime.defaultValue = dataEndDate
      }
    },

    getDefaultFilters(flowType = this.currentDataFlowType) {
      const filters = {}
      Object.entries(this.searchConfig).forEach(([key, config]) => {
        if (config.defaultValue !== undefined) {
          filters[key] = config.defaultValue
        }
      })
      if (flowType === 'inventory') {
        const inventoryDate = this.searchConfig.beginTime.inventoryDefaultValue
        filters.beginTime = inventoryDate
        filters.endTime = inventoryDate
      } else {
        filters.endTime = this.getEndTimeDefault(flowType)
      }
      return filters
    },

    formatSearchParams(data) {
      const params = { ...data }
      if (params.dataFlowType) {
        params.dataFlowType = Array.isArray(params.dataFlowType)
          ? params.dataFlowType[0]
          : params.dataFlowType
      }
      const config = FLOW_TYPE_CONFIG[params.dataFlowType] || FLOW_TYPE_CONFIG.sales
      Object.keys(params).forEach(key => {
        if (!config.filterFields.includes(key)) {
          delete params[key]
        }
      })
      if (params.beginTime) {
        params.beginTime = this.$dayjs(params.beginTime).valueOf()
      }
      if (params.endTime) {
        params.endTime = this.$dayjs(params.endTime).endOf('day').valueOf()
      }
      if (params.agencyKeys) {
        params.agencyKeys = Array.isArray(params.agencyKeys)
          ? params.agencyKeys
          : [params.agencyKeys]
      }
      if (params.companyNames) {
        params.companyNames = Array.isArray(params.companyNames)
          ? params.companyNames
          : [params.companyNames]
      }
      if (params.products) {
        const products = Array.isArray(params.products)
          ? params.products
          : [params.products]
        params.products = products.map(item => this.parseProductValue(item))
      }
      if (params.customs) {
        params.customs = Array.isArray(params.customs)
          ? params.customs
          : [params.customs]
      }
      if (params.suppliers) {
        params.suppliers = Array.isArray(params.suppliers)
          ? params.suppliers
          : [params.suppliers]
      }
      return params
    },

    normalizeFlowType(flowType = this.currentDataFlowType) {
      if (Array.isArray(flowType)) {
        return flowType[0]
      }
      return flowType || 'sales'
    },

    isCustomerItem(item) {
      return item && typeof item === 'object' && item.code && (item.name || item.customerName)
    },

    normalizeProvinceItem(item) {
      if (typeof item === 'string' || typeof item === 'number') {
        const text = String(item)
        return text ? { code: text, label: text } : null
      }
      if (!item || typeof item !== 'object') {
        return null
      }
      const text = this.extractOptionText(
        item.province ??
        item.provinceName ??
        item.customerProvince ??
        item.name ??
        item.label ??
        item.text ??
        item.value ??
        item.code
      )
      return text ? { code: text, label: text } : null
    },

    extractOptionText(value) {
      if (value == null || value === '') {
        return ''
      }
      if (typeof value === 'string' || typeof value === 'number') {
        return String(value)
      }
      if (typeof value === 'object') {
        return this.extractOptionText(
          value.name ??
          value.label ??
          value.province ??
          value.provinceName ??
          value.customerProvince ??
          value.code ??
          value.text ??
          value.value
        )
      }
      return ''
    },

    async loadCustomerProvinceOptions(flowType = this.currentDataFlowType) {
      try {
        const normalizedFlowType = this.normalizeFlowType(flowType)
        const res = await areaTree(undefined, normalizedFlowType)
        const data = res.data || {}
        let provinceList = []
        let customerList = []

        if (Array.isArray(data)) {
          if (data.length && data.every(item => typeof item === 'string' || typeof item === 'number')) {
            provinceList = data
          } else if (data.some(item => this.isCustomerItem(item))) {
            customerList = data
          } else {
            provinceList = data
          }
        } else {
          provinceList = data.customerProvinces || data.provinces || data.provinceList || []
          customerList = data.customers || data.agencyList || []
        }

        const dataEndDate = Array.isArray(data) ? '' : this.formatDataEndDate(data.dataEndTime)

        if (!provinceList.length && customerList.length) {
          const provinceSet = new Set()
          customerList.forEach(item => {
            const province = this.extractOptionText(
              typeof item === 'string' ? item : (
                item.province ?? item.provinceName ?? item.customerProvince
              )
            )
            if (province) {
              provinceSet.add(province)
            }
          })
          provinceList = [...provinceSet]
        }

        return {
          customerProvince: provinceList.map(item => this.normalizeProvinceItem(item)).filter(Boolean),
          agencyKeys: customerList.map(item => ({
            code: this.extractOptionText(item.code),
            label: this.extractOptionText(item.name ?? item.customerName)
          })).filter(item => item.code),
          dataEndDate
        }
      } catch (error) {
        console.error('获取DDI客户省份失败:', error)
        return { customerProvince: [], agencyKeys: [], dataEndDate: '' }
      }
    },

    async searchCompanyNames(keyword = '', flowType = this.currentDataFlowType) {
      try {
        const normalizedFlowType = this.normalizeFlowType(flowType)
        const params = keyword && keyword.trim() ? { customName: keyword.trim() } : {}
        const fetchFn = normalizedFlowType === 'purchase' ? companyPurchaseNameList : companySalesNameList
        const res = await fetchFn(params)
        const list = Array.isArray(res.data) ? res.data : []
        return list.map(item => ({
          value: item,
          text: item
        }))
      } catch (error) {
        console.error('搜索客户名称失败:', error)
        return []
      }
    },

    async loadCompanyNameOptions(flowType = this.currentDataFlowType) {
      const options = await this.searchCompanyNames('', flowType)
      return options.map(item => ({
        code: item.value,
        label: item.text
      }))
    },

    formatProductLabel({ name, spec }) {
      return spec ? `${name} (${spec})` : name
    },

    mapProductOption(item) {
      return {
        value: JSON.stringify({ name: item.name, spec: item.spec || '' }),
        text: this.formatProductLabel(item)
      }
    },

    parseProductValue(value) {
      if (typeof value === 'object' && value !== null) {
        return value
      }
      if (typeof value === 'string') {
        try {
          return JSON.parse(value)
        } catch (error) {
          return { name: value, spec: '' }
        }
      }
      return value
    },

    async searchProducts(keyword = '', flowType = this.currentDataFlowType) {
      try {
        const normalizedFlowType = this.normalizeFlowType(flowType)
        const params = keyword && keyword.trim() ? { productName: keyword.trim() } : {}
        const fetchFn = normalizedFlowType === 'purchase' ? productPurchaseNameList : productSalesNameList
        const res = await fetchFn(params)
        const list = Array.isArray(res.data) ? res.data : []
        return list.map(item => this.mapProductOption(item))
      } catch (error) {
        console.error('搜索产品名称失败:', error)
        return []
      }
    },

    async loadProductOptions(flowType = this.currentDataFlowType) {
      const options = await this.searchProducts('', flowType)
      return options.map(item => ({
        code: item.value,
        label: item.text
      }))
    },

    async searchSuppliers(keyword = '', flowType = this.currentDataFlowType) {
      try {
        const normalizedFlowType = this.normalizeFlowType(flowType)
        const params = keyword && keyword.trim() ? { customName: keyword.trim() } : {}
        const fetchFn = normalizedFlowType === 'purchase'
          ? suppliersPurchaseNameList
          : customerSalesNameList
        const res = await fetchFn(params)
        const list = Array.isArray(res.data) ? res.data : []
        return list.map(item => ({
          value: item,
          text: item
        }))
      } catch (error) {
        console.error('搜索供应商名称失败:', error)
        return []
      }
    },

    async loadSupplierOptions(flowType = this.currentDataFlowType) {
      const options = await this.searchSuppliers('', flowType)
      return options.map(item => ({
        code: item.value,
        label: item.text
      }))
    },

    async searchCustoms(keyword = '', flowType = this.currentDataFlowType) {
      try {
        const normalizedFlowType = this.normalizeFlowType(flowType)
        const params = keyword && keyword.trim() ? { customName: keyword.trim() } : {}
        const fetchFn = normalizedFlowType === 'purchase' ? customerPurchaseNameList : customerSalesNameList
        const res = await fetchFn(params)
        const list = Array.isArray(res.data) ? res.data : []
        return list.map(item => ({
          value: item,
          text: item
        }))
      } catch (error) {
        console.error('搜索买方客户名称失败:', error)
        return []
      }
    },

    async loadCustomOptions(flowType = this.currentDataFlowType) {
      const options = await this.searchCustoms('', flowType)
      return options.map(item => ({
        code: item.value,
        label: item.text
      }))
    },

    mapSearchListItems(searchList, options, flowType) {
      const normalizedFlowType = this.normalizeFlowType(flowType)
      const config = FLOW_TYPE_CONFIG[normalizedFlowType] || FLOW_TYPE_CONFIG.sales
      return searchList
        .filter(item => config.filterFields.includes(item.name))
        .map(item => {
          if (item.name === 'dataFlowType') {
            return {
              ...item,
              isNotClearable: true,
              onChange: (value) => this.onDataFlowTypeChange(value)
            }
          }
          if (item.name === 'customerProvince') {
            return {
              ...item,
              optionSource: null,
              options: options.customerProvince
            }
          }
          if (item.name === 'agencyKeys') {
            return {
              ...item,
              optionSource: null,
              options: options.agencyKeys
            }
          }
          if (item.name === 'companyNames') {
            return {
              ...item,
              optionSource: null,
              options: options.companyNames,
              isSearch: true,
              remoteSearch: (keyword) => this.searchCompanyNames(keyword, flowType)
            }
          }
          if (item.name === 'products') {
            return {
              ...item,
              optionSource: null,
              options: options.products,
              isSearch: true,
              remoteSearch: (keyword) => this.searchProducts(keyword, normalizedFlowType)
            }
          }
          if (item.name === 'customs') {
            return {
              ...item,
              optionSource: null,
              options: options.customs,
              isSearch: true,
              remoteSearch: (keyword) => this.searchCustoms(keyword, normalizedFlowType)
            }
          }
          if (item.name === 'suppliers') {
            return {
              ...item,
              optionSource: null,
              options: options.suppliers,
              isSearch: true,
              remoteSearch: (keyword) => this.searchSuppliers(keyword, normalizedFlowType)
            }
          }
          return item
        })
    },

    async loadSearchOptions(flowType = this.currentDataFlowType) {
      const normalizedFlowType = this.normalizeFlowType(flowType)
      const [
        provinceOptions,
        productOptions,
        companyNameOptions,
        customOptions,
        supplierOptions
      ] = await Promise.all([
        this.loadCustomerProvinceOptions(normalizedFlowType),
        this.loadProductOptions(normalizedFlowType),
        this.loadCompanyNameOptions(normalizedFlowType),
        this.loadCustomOptions(normalizedFlowType),
        this.loadSupplierOptions(normalizedFlowType)
      ])

      return {
        customerProvince: provinceOptions.customerProvince,
        agencyKeys: provinceOptions.agencyKeys,
        companyNames: companyNameOptions,
        products: productOptions,
        customs: customOptions,
        suppliers: supplierOptions,
        dataEndDate: provinceOptions.dataEndDate
      }
    },

    async ensureFilterOptions(flowType = this.currentDataFlowType) {
      const normalizedFlowType = this.normalizeFlowType(flowType)
      if (!this.filterOptionsCache[normalizedFlowType]) {
        this.filterOptionsCache[normalizedFlowType] = await this.loadSearchOptions(normalizedFlowType)
      }
      this.applyEndTimeDefault(normalizedFlowType)
      return this.filterOptionsCache[normalizedFlowType]
    },

    async rebuildSearchList(flowType) {
      const normalizedFlowType = this.normalizeFlowType(flowType)
      const config = FLOW_TYPE_CONFIG[normalizedFlowType] || FLOW_TYPE_CONFIG.sales
      const baseList = Object.values(mockMeta[config.metaKey].filters)
        .filter(item => config.filterFields.includes(item.name))
        .sort((a, b) => a.index - b.index)
      const options = this.getFilterOptionsForFlowType(normalizedFlowType)
      this.searchList = this.mapSearchListItems(baseList, options, normalizedFlowType)
    },

    async onDataFlowTypeChange(flowType) {
      const normalizedFlowType = this.normalizeFlowType(flowType)
      if (!normalizedFlowType) {
        return
      }
      await this.rebuildSearchList(normalizedFlowType)
      const config = FLOW_TYPE_CONFIG[normalizedFlowType] || FLOW_TYPE_CONFIG.sales
      const searchBox = this.$refs.searchBox
      if (searchBox) {
        Object.keys(searchBox.formModel).forEach(key => {
          if (!config.filterFields.includes(key)) {
            this.$delete(searchBox.formModel, key)
          }
        })
        searchBox.formModel.customerProvince = undefined
        searchBox.formModel.agencyKeys = []
        if (normalizedFlowType === 'inventory') {
          const inventoryDate = this.searchConfig.beginTime.inventoryDefaultValue
          searchBox.formModel.beginTime = inventoryDate
          searchBox.formModel.endTime = inventoryDate
        } else {
          this.applyEndTimeDefault(normalizedFlowType)
          searchBox.formModel.beginTime = this.searchConfig.beginTime.defaultValue
          searchBox.formModel.endTime = this.getEndTimeDefault(normalizedFlowType)
        }
      }
    },

    // 元数据加载完成
    async handleMetaLoaded({ searchList }) {
      const flowType = this.normalizeFlowType(this.currentDataFlowType)
      if (!this.pendingSearchParams && !this.filterOptionsCache[flowType]) {
        await this.ensureFilterOptions(flowType)
      }
      this.searchList = this.mapSearchListItems(
        searchList,
        this.getFilterOptionsForFlowType(flowType),
        flowType
      )

      this.$nextTick(() => {
        const params = this.pendingSearchParams || this.formatSearchParams(this.getDefaultFilters())
        this.pendingSearchParams = null
        this.$refs.cardList.search(params)
      })
    },

    // 搜索
    handleSearch (data) {
      const params = this.formatSearchParams(data)
      const newType = params.dataFlowType || 'sales'

      this.$refs.drawer.hide()

      if (newType !== this.currentDataFlowType) {
        this.currentDataFlowType = newType
        this.pendingSearchParams = params
        return
      }

      this.$refs.cardList.search(params)
    },

    // 重置
    async handleReset(formModel) {
      this.$refs.drawer.hide()
      const params = this.formatSearchParams(formModel)
      const newType = params.dataFlowType || 'sales'

      if (newType !== this.currentDataFlowType) {
        this.currentDataFlowType = newType
        this.pendingSearchParams = params
        await this.ensureFilterOptions(newType)
        await this.rebuildSearchList(newType)
        await this.$nextTick()
        if (this.$refs.searchBox) {
          this.$refs.searchBox.initForm()
        }
        return
      }

      this.$refs.cardList.search(params)
    },

    // 卡片点击
    handleCardClick(item) {
      this.$router.push({
        name: 'FormTableDetail',
        params: {
          rowData: item
        },
        query: {
          whereKey: this.rowKey,
          [this.rowKey]: item[this.rowKey],
          formKey: this.formKey,
          title: this.currentTitle,
          dataFlowType: this.currentDataFlowType,
          manualMode: this.manualMode,
          fromRoute: 'TerminalSalesAnalysis'
        }
      })
    },

    // 格式化字段值
    formatNumber(fieldName, value) {
      return this.$refs.cardList?.formatValue(fieldName, value) ?? value ?? '-'
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/theme-variables.styl'

.orig-sales-flow-card
  .filter-icon
    display inline-flex
    align-items center
    justify-content center
    padding 0 20px
    height 100%
    cursor pointer

.page-content
  height 100%
  background $page-bg-gradient

.sales-flow-card
  background $card-bg
  border-radius $border-radius-item
  overflow hidden
  box-shadow 0 2px 12px rgba(100, 116, 139, 0.08)
  margin-bottom 20px

  .card-header
    background $card-header-gradient
    padding 16px $spacing-page
    color #ffffff

    .header-top
      display flex
      justify-content space-between
      align-items center

      .flow-code
        font-size 26px
        font-weight 600

      .flow-date
        font-size 24px
        font-weight 500

  .card-body
    padding $spacing-page

    // 客户信息区
    .customer-section
      margin-bottom 20px
      padding-bottom 16px
      border-bottom 1px solid $border-normal

      .customer-main
        display flex
        align-items center
        gap 8px
        margin-bottom 12px

        .customer-name
          font-size 30px
          font-weight 600
          color $text-primary

      .customer-flow
        display flex
        align-items center
        gap 8px
        font-size 24px
        margin-left 26px
        margin-bottom 6px

        .flow-label
          color $text-secondary
          flex-shrink: 0

        .flow-value
          color $text-primary

    // 产品信息区
    .product-section
      margin-bottom 20px

      .product-main
        display flex
        align-items center
        gap 8px
        margin-bottom 8px

        .product-name
          font-size 28px
          font-weight 500
          color $text-primary

      .product-detail
        font-size 24px
        color $text-secondary
        margin-left 26px

        .separator
          margin 0 8px

    // 数据高亮区域
    .data-highlight
      background $item-bg-gradient
      border-radius $border-radius-small
      padding 16px
      display flex
      justify-content space-around
      align-items center

      .data-item
        text-align center
        flex 1

        .data-value
          font-size 32px
          font-weight 700
          color $text-primary
          margin-bottom 8px
          height 48px
          &.highlight
            color $primary-blue

        .data-label
          font-size 22px
          color $text-secondary
</style>
